import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

interface PageContextValue {
  data: unknown;
  setData: (data: unknown) => void;
}

const PageContext = createContext<PageContextValue>({ data: undefined, setData: () => {} });

export function PageContextProvider({ children }: PropsWithChildren) {
  const [data, setDataState] = useState<unknown>(undefined);
  const locationRef = useRef<string>('');
  const location = useLocation();

  const setData = useCallback(
    (newData: unknown) => {
      setDataState(newData);
      locationRef.current = location.pathname;
    },
    [location.pathname]
  );

  // Clear context when navigating to a different page
  useEffect(() => {
    if (location.pathname !== locationRef.current) {
      locationRef.current = location.pathname;
      setDataState(undefined);
    }
  }, [location.pathname]);

  return <PageContext.Provider value={{ data, setData }}>{children}</PageContext.Provider>;
}

/**
 * Register page-level API response data for the assistant chat context.
 * Call this in detail page components after data is loaded.
 */
export function useRegisterPageContext(data: unknown) {
  const { setData } = useContext(PageContext);

  useEffect(() => {
    if (data !== undefined) {
      setData(data);
    }
  }, [data, setData]);
}

/**
 * Read the current page context data. Used by the chat widget.
 */
export function usePageContext(): unknown {
  return useContext(PageContext).data;
}
